import { type LoaderFunctionArgs, type MetaFunction } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { type HeadersFunction } from '@remix-run/server-runtime'
import { GraphQLClient } from 'graphql-request'
import { Articles } from '#app/components/articleCard.tsx'
import { ArticleGrid } from '#app/components/articleGrid.tsx'
import { ArticlePage } from '#app/components/articlePage'
import NewsLetterSignup from '#app/components/newsLetterSignup.tsx'
import { PaginationBar } from '#app/components/paginationBar.tsx'
import { PostTypes, fallback } from '#app/config/constants'
import newsletterSignupBackground from '#app/images/grape_varietals_newsletter.jpg'
import { getUrl, reuseUsefulLoaderHeaders } from '#app/utils/misc'
import { getSocialMetas } from '#app/utils/seo'
import PageQuery from '../../libs/queries/PageQuery.graphql'
import PostsQuery from '../../libs/queries/PostsQuery.graphql'
import { type Posts } from '../wine-blog+'

export const loader = async ({ request }: LoaderFunctionArgs) => {
	const url = new URL(request.url)
	const $top = Number(url.searchParams.get('$top')) || 10
	const $skip = Number(url.searchParams.get('$skip')) || 0

	const hygraphUrl = process.env.CMS_URL
	const hygraphToken = process.env.CMS_API_TOKEN

	if (!hygraphUrl || !hygraphToken) {
		throw new Error('Missing configuration for graphQL client')
	}

	const hygraph = new GraphQLClient(hygraphUrl, {
		headers: {
			authorization: `Bearer ${hygraphToken}`,
		},
	})

	const {
		postsConnection: {
			edges: rawPosts,
			aggregate: { count },
		},
	} = await hygraph.request<Posts>(PostsQuery, {
		posttype: PostTypes['GrapeVarietals'],
		per_page: 10,
		skip: $skip,
	})

	const { page } = await hygraph.request<any>(PageQuery, {
		slug: 'grape-varietals',
	})

	if (!rawPosts) {
		throw new Error('Error loading data')
	}

	const posts = rawPosts.map(p => p.node)

	return { page, posts, count }
}

export const headers: HeadersFunction = reuseUsefulLoaderHeaders

export const meta: MetaFunction = ({ data, matches }: any) => {
	const {
		data: { requestInfo },
	} = matches.find((match: any) => match.id === 'root')

	console.log('SEO in data: ', data, data.seo)
	console.log(''.padEnd(100, '-'))

	const {
		page: { seo },
	} = data

	return getSocialMetas({
		title: seo ? seo.metaTitle : fallback['metaTitle'],
		description: seo ? seo.metaDescription : fallback['metaDescription'],
		url: getUrl(requestInfo),
		image: seo.ogImage.url,
	})
}

export default function VarietalsRoute() {
	const {
		posts,
		page: { main },
		count,
	} = useLoaderData()

	return (
		<>
			<ArticlePage main={main}>
				<ArticleGrid>
					<Articles posts={posts} />
				</ArticleGrid>
			</ArticlePage>
			<PaginationBar total={count} />
			<NewsLetterSignup
				newsLetterBackgroundImage={newsletterSignupBackground}
			/>
		</>
	)
}
